import axios from 'axios';
import { auth, db, getEffectiveUserId } from '../../firebase';
import { sanitizeText } from './textUtils';
import { getPromptByLanguage, defaultLanguage } from '../../languages/prompts';
import { doc, getDoc } from 'firebase/firestore';

/**
 * AI service til at generere tekst via Firebase Function (bruger nu OpenAI)
 */
class ClaudeService {
  /**
   * Genererer respons fra OpenAI ved at kalde Cloud Function
   * 
   * @param {string} prompt - Prompt til AI
   * @param {string} systemPrompt - System prompt (optional)
   * @param {string} language - Sprog kode (optional, default hentes fra brugerens indstillinger)
   * @returns {Promise<string>} - Den genererede tekst
   */
  async generateResponse(prompt, systemPrompt = null, language = null) {
    try {
      console.log('Genererer OpenAI svar via Firebase Function til prompt:', prompt);
      console.log('Sprog parameter sendt til AI Service:', language);
      
      // Sanitér prompt for at undgå Unicode-problemer
      const sanitizedPrompt = sanitizeText(prompt);
      
      let postLanguage = language || defaultLanguage;
      console.log('Initialt sprog:', postLanguage, language ? '(valgt direkte)' : '(standardsprog)');
      
      // Hvis language ikke er angivet, hent fra brugerens indstillinger
      if (!language && auth.currentUser) {
        try {
          // Hent brugerens selectedCustomer state fra global CustomerSwitchContext
          let userId = auth.currentUser.uid;
          const selectedCustomer = window.customerSwitchState?.selectedCustomer;
          
          // Hvis brugeren arbejder på vegne af en kunde, brug kundens ID i stedet
          if (selectedCustomer && selectedCustomer.id) {
            userId = selectedCustomer.id;
            console.log(`DEBUGGING: Læser sprogindstilling for kunde ${userId} i stedet for ${auth.currentUser.uid}`);
          }
          
          // Hent det valgte sprog fra brugerens indstillinger
          const settingsRef = doc(db, 'users', userId, 'settings', 'ai_prompts');
          const settingsSnap = await getDoc(settingsRef);
          
          if (settingsSnap.exists()) {
            const data = settingsSnap.data();
            if (data.postLanguage) {
              postLanguage = data.postLanguage;
              console.log(`DEBUGGING: Bruger sprog fra indstillinger: ${postLanguage} for bruger ${userId}`);
            }
          }
        } catch (error) {
          console.error('Fejl ved hentning af sprog fra brugerindstillinger:', error);
          // Fortsæt med standard sprog hvis der opstår en fejl
        }
      }
      
      console.log(`SPROG INFORMATION: Bruger '${postLanguage}' til generering af opslag`);
      
      // Hent sprogspecifikke prompts baseret på det valgte sprog
      const languagePrompts = getPromptByLanguage(postLanguage);
      
      // Hvis der ikke er angivet en systempromt, brug en sprogspecifik standard
      const finalSystemPrompt = systemPrompt || languagePrompts.systemPrompt;
      
      console.log(`SPROG DETALJER: Bruger ${systemPrompt ? 'direkte angivet systemPrompt' : 'systemPrompt fra sprogfil for ' + postLanguage}`);

      // Forbered request data
      const requestData = {
        prompt: sanitizedPrompt,
        systemPrompt: finalSystemPrompt,
        language: postLanguage // Send sprog information til Cloud Function
      };
      
      // Tilføj bruger-ID hvis brugeren er logget ind
      if (auth.currentUser) {
        requestData.userId = auth.currentUser.uid;
      }

      // Brug axios til at kalde OpenAI Proxy direkte som HTTP endepunkt
      const response = await axios.post(
        'https://us-central1-app-nuvik.cloudfunctions.net/openaiProxy',
        requestData,
        {
        headers: {
            'Content-Type': 'application/json'
        }
        }
      );

      // Tjek om responsen indeholder content
      if (!response.data || !response.data.content || response.data.content.length === 0) {
        throw new Error('Intet indhold i responsen fra OpenAI API');
      }
      
      // Udpak og returner teksten fra responsen
      return response.data.content[0].text;
    } catch (error) {
      console.error('Fejl ved generering af svar med OpenAI:', error);
      
      // Håndter HTTP fejl (axios fejl)
      if (error.response) {
        // Server svarede med en status kode uden for 2xx intervallet
        console.error('Server svarede med fejl:', error.response.status, error.response.data);
        throw new Error(`OpenAI API fejl: ${error.response.status} - ${error.response.data.error || 'Ukendt fejl'}`);
      } else if (error.request) {
        // Anmodningen blev sendt, men intet svar blev modtaget
        console.error('Ingen respons fra serveren:', error.request);
        throw new Error('Ingen respons fra serveren. Tjek din internetforbindelse.');
      } else if (error.message) {
        // Der skete en fejl ved opsætning af anmodningen
        throw new Error(`Fejl ved generering af svar: ${error.message}`);
      } else {
        // En ukendt fejl
        throw new Error('Ukendt fejl ved generering af svar fra OpenAI');
      }
    }
  }
}

// Eksporter en singleton-instans af servicen
export default new ClaudeService(); 